import { Component } from '@theme/component';

/**
 * A custom element that manages a gift card recipient form.
 *
 * @typedef {object} GiftCardRecipientFormRefs
 * @property {HTMLTextAreaElement} messageTextarea - The message textarea input.
 * @property {HTMLElement} charCountCurrent - The current character count display.
 * @property {HTMLInputElement} sendOnDateInput - The send on date input.
 *
 * @extends Component<GiftCardRecipientFormRefs>
 */
class GiftCardRecipientForm extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { messageTextarea, charCountCurrent, sendOnDateInput } = this.refs;
    const maxLength = 200;

    if (messageTextarea && charCountCurrent) {
      messageTextarea.addEventListener('input', () => {
        const currentLength = messageTextarea.value.length;
        charCountCurrent.textContent = String(currentLength);

        if (currentLength > maxLength) {
          messageTextarea.setAttribute('aria-invalid', 'true');
          messageTextarea.style.borderColor = 'var(--color-error)';
        } else {
          messageTextarea.removeAttribute('aria-invalid');
          messageTextarea.style.borderColor = '';
        }
      });

      charCountCurrent.textContent = String(messageTextarea.value.length);
    }

    if (sendOnDateInput) {
      const pastErrorMsg = sendOnDateInput.dataset.pastError || 'Send date cannot be in the past';
      const futureErrorMsg = sendOnDateInput.dataset.futureError || 'Send date cannot be more than 90 days in the future';

      sendOnDateInput.addEventListener('change', () => {
        const selectedDate = new Date(sendOnDateInput.value);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const maxDate = new Date();
        maxDate.setDate(today.getDate() + 90);

        if (selectedDate < today) {
          sendOnDateInput.setCustomValidity(pastErrorMsg);
        } else if (selectedDate > maxDate) {
          sendOnDateInput.setCustomValidity(futureErrorMsg);
        } else {
          sendOnDateInput.setCustomValidity('');
        }
      });
    }
  }
}

if (!customElements.get('gift-card-recipient-form')) {
  customElements.define('gift-card-recipient-form', GiftCardRecipientForm);
}
